/**
 * /dashboard/change-requests/$id — Change-request detail page (G7.5).
 *
 * Backend: GET /api/v1/change-requests/{id}. Visibility: HR-admin OR the
 * request's employee OR the employee's manager-of (server-enforced).
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/change-requests/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useChangeRequest } from "@/hooks/use-change-request";
import { PageHeader } from "@/components/shared/page-header";
import { ChangeRequestDetail } from "@/components/change-requests/change-request-detail";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { typeLabel } from "@/lib/change-request-format";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/change-requests/$id",
)({
  component: ChangeRequestDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

function ChangeRequestDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useChangeRequest(numId, validId);

  return (
    <>
      <PageHeader
        title="Change request"
        description={
          q.data ? `${typeLabel(q.data.request_type)} · #${q.data.id}` : undefined
        }
        actions={
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/change-requests" params={{ tenantSlug }}>
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid request id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid change-request id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this change request</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/change-requests" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to change requests
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && <ChangeRequestDetail request={q.data} />}
    </>
  );
}
